import { Injectable } from '@angular/core';
import { Observable, map, of, switchMap } from 'rxjs';
import { Payable } from '../../models/entities/payable';
import { LedgerEntry } from '../../models/entities/ledger-entry';
import { PayablesService } from './payables.service';
import { TransactionsService } from '../accounting/transactions.service';

/** The model type core reports on a fulfillment reference that is a Transaction. */
export const TRANSACTION_REFERENCE_MODEL_TYPE = 'Transaction';

/**
 * What a ledger line shows when its account no longer loads: core eager-loads a ledger entry's
 * account without trashed rows, so a since-deleted account arrives as null.
 */
export const DELETED_ACCOUNT_NAME = 'Deleted account';

/** One line of a payable's live payment entry, in cents. */
export interface PaymentEntryLine {
    accountName: string;
    debit: number;
    credit: number;
}

/**
 * Resolves the ledger lines that currently represent a payable's payment (SD-9005), for the void
 * dialog to show what the void will reverse.
 *
 * Every payment type's entry lives on one Transaction:
 *  - a marked-as-paid match (a kept journal entry, a replaced one, or a regular transaction) is the
 *    fulfillment's own reference;
 *  - every other type is the payable's non-journal-entry transaction.
 */
@Injectable({
    providedIn: 'root',
})
export class PayablePaymentEntryService {
    constructor(
        private _payablesService: PayablesService,
        private _transactionsService: TransactionsService,
    ) {
    }

    /**
     * The payment transaction's id, or null when it can't be identified with certainty.
     */
    static resolvePaymentTransactionId(payable: Payable): number | null {
        const reference = payable?.currentFulfillment?.fulfillmentReference;
        if (reference?.modelType === TRANSACTION_REFERENCE_MODEL_TYPE && reference.id) {
            return reference.id;
        }

        const transactions = payable?.transactions ?? [];

        // A journal entry on the payable means it was voided before: it is a Void/Cancel
        // counter-entry, or an earlier payment's kept journal entry. Paying again reuses the first
        // payment's transaction rather than writing a new one, so the transaction found below may
        // be the payment that was already reversed, not this one. Show nothing rather than guess.
        if (transactions.some((transaction) => transaction.journalEntry)) {
            return null;
        }

        const paymentTransactions = transactions
            .filter((transaction) => !transaction.journalEntry)
            .sort((a, b) => b.id - a.id);

        return paymentTransactions[0]?.id ?? null;
    }

    /**
     * The transaction's live lines: every line still standing once each reversal has cancelled the
     * line it reversed.
     *
     * A transaction's entries are its whole history, not just what is live: a replaced journal entry
     * still carries its original lines and their reversal beside the standard payment entry, and an
     * edit is a reversal plus a repost. A reversal swaps a line's debit and credit, so a line is
     * cancelled by the opposite-side line for the same account, unit and amount. The lines are not
     * netted per account: a live entry may debit and credit the same account (between two units), and
     * the void reverses each of those lines.
     */
    static liveLedgerLines(entries: LedgerEntry[]): PaymentEntryLine[] {
        const standing: { entry: LedgerEntry, amount: number }[] = [];

        for (const entry of entries ?? []) {
            const amount = (entry.debit ?? 0) - (entry.credit ?? 0);
            if (amount === 0) {
                continue;
            }

            const reversed = standing.findIndex((line) => line.amount === -amount
                && line.entry.accountType === entry.accountType
                && line.entry.accountId === entry.accountId
                && (line.entry.unitId ?? null) === (entry.unitId ?? null));

            if (reversed === -1) {
                standing.push({ entry, amount });
            } else {
                standing.splice(reversed, 1);
            }
        }

        const lines: PaymentEntryLine[] = standing.map(({ entry, amount }) => ({
            accountName: PayablePaymentEntryService.accountName(entry),
            debit: amount > 0 ? amount : 0,
            credit: amount < 0 ? -amount : 0,
        }));

        // Debits above credits, the way a journal entry reads.
        return [
            ...lines.filter((line) => line.debit > 0),
            ...lines.filter((line) => line.credit > 0),
        ];
    }

    /**
     * The payable's live payment entry lines on the org's current accounting basis. Empty when the
     * payment transaction can't be identified or carries no ledger entries (e.g. Accounting V1).
     *
     * The payable is re-read only when the one passed in lacks what resolution needs: the list and
     * detail endpoints both return its transactions and fulfillment reference, but a payable built
     * from elsewhere (a transaction's reference) may not carry them.
     */
    getPaymentEntryLines(payable: Payable): Observable<PaymentEntryLine[]> {
        const payable$ = PayablePaymentEntryService.canResolve(payable)
            ? of(payable)
            : this._payablesService.getPayable(payable.id);

        return payable$.pipe(
            switchMap((resolvable) => {
                const transactionId = PayablePaymentEntryService.resolvePaymentTransactionId(resolvable);
                if (transactionId === null) {
                    return of([]);
                }

                return this._transactionsService.getTransaction(resolvable.organizationId, transactionId).pipe(
                    map((transaction) => PayablePaymentEntryService.liveLedgerLines(transaction.ledgerEntries)),
                );
            }),
        );
    }

    private static canResolve(payable: Payable): boolean {
        const fulfillment = payable?.currentFulfillment;
        if (!fulfillment) {
            return false;
        }

        return fulfillment.fulfillmentReference?.modelType === TRANSACTION_REFERENCE_MODEL_TYPE
            || Array.isArray(payable.transactions);
    }

    private static accountName(entry: LedgerEntry): string {
        const account = entry.account as { name?: string, label?: string };

        return account?.name || account?.label || DELETED_ACCOUNT_NAME;
    }
}
